import { useState } from "react";
import { Bot, DollarSign, Activity, ChevronUp, X, Settings2, History, Gauge } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface StockInfo {
  id?: number;
  code: string;
  name: string;
  status: string;
  profit: number;
  holding: number;
}

interface EngineStatus {
  total: number;
  running: number;
  idle: number;
  todayProfit: number;
  todayTrades: number;
  todayRounds?: number;
  todayAmount?: number;
  netPct?: number;
  byClass?: {
    stock: { rounds: number; trades: number; amount: number; profit: number };
    etf: { rounds: number; trades: number; amount: number; profit: number };
  };
  stocks: StockInfo[];
}

interface MobileQuickBarProps {
  engineStatus: EngineStatus;
  visible?: boolean;
  onStockAction?: (stock: StockInfo, action: "params" | "records" | "detail") => void;
  // [20260717 Steven] 悬浮条实盘/模拟动态切换
  scope?: 'all' | 'live' | 'sim';
  onScopeChange?: (s: 'all' | 'live' | 'sim') => void;
}

export default function MobileQuickBar({ engineStatus, visible = true, onStockAction, scope = 'all', onScopeChange }: MobileQuickBarProps) {
  const [expanded, setExpanded] = useState(false);

  if (!visible) return null;

  const { total, running, todayProfit, todayTrades, todayRounds, todayAmount, netPct, byClass, stocks } = engineStatus;
  const fmtWan = (v: number) => v >= 10000 ? `${(v / 10000).toFixed(1)}万` : String(Math.round(v));
  const profitColor = todayProfit >= 0 ? "text-gain" : "text-loss";
  const netPctColor = (netPct ?? 0) >= 0 ? "text-gain" : "text-loss";

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
      {/* 展开面板 */}
      {expanded && (
        <div className="bg-card/95 backdrop-blur-lg border-t border-border/50 max-h-[50vh] overflow-y-auto">
          <div className="p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">引擎状态详情（点击操作）</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setExpanded(false)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            {stocks.length > 0 ? (
              <div className="space-y-1.5">
                {stocks.map((s) => (
                  <div key={s.code} className="rounded-md bg-muted/30 overflow-hidden">
                    <div className="flex items-center justify-between p-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${s.status === "running" ? "bg-emerald-400 animate-pulse" : "bg-muted-foreground"}`} />
                        <span className="text-xs font-mono text-muted-foreground">{s.code}</span>
                        <span className="text-xs font-medium">{s.name}</span>
                        {s.holding > 0 && (
                          <Badge variant="secondary" className="text-[9px] px-1 h-4">持{s.holding}</Badge>
                        )}
                      </div>
                      <span className={`text-xs font-mono font-medium ${s.profit >= 0 ? "text-gain" : "text-loss"}`}>
                        {s.profit >= 0 ? "+" : ""}¥{s.profit.toFixed(2)}
                      </span>
                    </div>
                    {/* 快捷操作按钮 */}
                    {onStockAction && (
                      <div className="flex items-center gap-1 px-2 pb-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[10px] flex-1"
                          onClick={(e) => { e.stopPropagation(); onStockAction(s, "detail"); }}
                        >
                          <Gauge className="h-3 w-3 mr-1 text-blue-400" />状态
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[10px] flex-1"
                          onClick={(e) => { e.stopPropagation(); onStockAction(s, "params"); }}
                        >
                          <Settings2 className="h-3 w-3 mr-1 text-primary" />参数
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[10px] flex-1"
                          onClick={(e) => { e.stopPropagation(); onStockAction(s, "records"); }}
                        >
                          <History className="h-3 w-3 mr-1 text-amber-400" />记录
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-xs text-muted-foreground">暂无运行中的引擎</div>
            )}
          </div>
        </div>
      )}

      {/* 底部栏 */}
      <div
        className="bg-card/95 backdrop-blur-lg border-t border-border/50 px-3 py-2 flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Bot className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-medium">{running}/{total}</span>
            {running > 0 && (
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            )}
          </div>
          {onScopeChange && (
            <button
              onClick={(e) => { e.stopPropagation(); onScopeChange(scope === 'all' ? 'live' : scope === 'live' ? 'sim' : 'all'); }}
              className={`px-1.5 py-0.5 rounded border text-[10px] font-medium ${scope === 'live' ? 'bg-red-500/15 text-red-400 border-red-500/40' : scope === 'sim' ? 'bg-blue-500/15 text-blue-400 border-blue-500/40' : 'bg-muted/40 text-muted-foreground border-border/50'}`}
            >{scope === 'all' ? '全部' : scope === 'live' ? '实盘' : '模拟'}</button>
          )}
          <div className="h-3 w-px bg-border/50" />
          <div className="flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-muted-foreground" />
            <span className={`text-xs font-mono font-medium ${profitColor}`}>
              {todayProfit >= 0 ? "+" : ""}¥{Math.round(todayProfit)}
            </span>
          </div>
          {todayTrades > 0 && (
            <>
              <div className="h-3 w-px bg-border/50" />
              <div className="flex items-center gap-1">
                <Activity className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{todayTrades}笔{(todayRounds ?? 0) > 0 ? ` · ${todayRounds}轮` : ""}</span>
              </div>
            </>
          )}
          {(todayAmount ?? 0) > 0 && (
            <>
              <div className="h-3 w-px bg-border/50" />
              <div className="flex items-center gap-0.5 text-xs text-muted-foreground font-mono">
                <span>¥{(todayAmount ?? 0) >= 10000 ? `${((todayAmount ?? 0) / 10000).toFixed(1)}万` : Math.round(todayAmount ?? 0)}</span>
                <span className={`ml-1 ${netPctColor}`}>
                  {(netPct ?? 0) >= 0 ? "+" : ""}{(netPct ?? 0).toFixed(3)}%
                </span>
              </div>
            </>
          )}
        </div>
        <ChevronUp className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
      </div>
      {/* [20260717 Steven] 股/ETF细分行: 并排即读可比(拒绝上下轮换) */}
      {byClass && (byClass.stock.trades > 0 || byClass.etf.trades > 0) && (
        <div
          className="bg-card/95 backdrop-blur-lg px-3 pb-1.5 -mt-0.5 flex items-center gap-2 text-[10px] font-mono cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <span className="px-1 rounded bg-blue-500/15 text-blue-400 font-sans">股</span>
          <span className="text-muted-foreground">{byClass.stock.rounds}轮 ¥{fmtWan(byClass.stock.amount)}</span>
          <span className={byClass.stock.profit >= 0 ? "text-gain" : "text-loss"}>{byClass.stock.profit >= 0 ? "+" : ""}{Math.round(byClass.stock.profit)}</span>
          <div className="h-3 w-px bg-border/50" />
          <span className="px-1 rounded bg-purple-500/15 text-purple-400 font-sans">ETF</span>
          <span className="text-muted-foreground">{byClass.etf.rounds}轮 ¥{fmtWan(byClass.etf.amount)}</span>
          <span className={byClass.etf.profit >= 0 ? "text-gain" : "text-loss"}>{byClass.etf.profit >= 0 ? "+" : ""}{Math.round(byClass.etf.profit)}</span>
        </div>
      )}
    </div>
  );
}
